import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CollabBadge } from "@/components/CollabBadge";
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, Loader2, Activity, Zap,
} from "lucide-react";

// Metrics available on the standings dropdown. Must stay in sync with the
// backend `_STANDING_METRICS` set. `kind` controls formatting.
const METRICS = [
  { key: "views",            label: "Views",           kind: "int" },
  { key: "subscribers",      label: "Subscribers",     kind: "int" },
  { key: "members",          label: "Members",         kind: "int" },
  { key: "followers",        label: "Followers",       kind: "int" },
  { key: "revenue",          label: "Revenue",         kind: "currency" },
  { key: "engagement_rate",  label: "Engagement Rate", kind: "percent" },
  { key: "watch_time_hours", label: "Watch Time",      kind: "hours" },
  { key: "comments",         label: "Comments",        kind: "int" },
  { key: "shares",           label: "Shares",          kind: "int" },
];

const REFRESH_MS = 30_000;

const CATEGORY_COLORS = {
  Talk: "#007AFF", Music: "#FF3B30", Sports: "#32D74B", Podcast: "#FFD60A",
  News: "#AF52DE", Comedy: "#FF9500", Interview: "#5AC8FA", "Live Event": "#FF2D92",
};

function formatMetric(kind, val) {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return "—";
  if (kind === "currency") {
    if (n >= 1e6) return "$" + (n / 1e6).toFixed(2).replace(/\.00$/, "") + "M";
    if (n >= 1e3) return "$" + (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    return "$" + n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (kind === "percent") return n.toFixed(2).replace(/\.00$/, "") + "%";
  const suffix = kind === "hours" ? " hrs" : "";
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B" + suffix;
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M" + suffix;
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K" + suffix;
  return n.toLocaleString() + suffix;
}

function medalColor(pos) {
  if (pos === 0) return "#FFD60A";
  if (pos === 1) return "#C0C6CC";
  if (pos === 2) return "#CD7F32";
  return "var(--ff-text-dim)";
}

export default function LiveStandings() {
  const [metric, setMetric] = useState("views");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const timerRef = useRef(null);

  const load = async (opts = {}) => {
    if (opts.silent) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/admin/standings?metric=${metric}`);
      setData(data);
      setCountdown(REFRESH_MS / 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load standings");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  // Load whenever metric changes.
  useEffect(() => { load(); }, [metric]); // eslint-disable-line react-hooks/exhaustive-deps

  // 30s auto-refresh + countdown.
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          load({ silent: true });
          return REFRESH_MS / 1000;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [metric]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeMetric = useMemo(() => METRICS.find((m) => m.key === metric) || METRICS[0], [metric]);
  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;

  return (
    <div className="space-y-6" data-testid="live-standings-page">
      <div>
        <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
          <Activity size={12} /> ▮ ADMIN / LIVE STANDINGS
        </div>
        <h2 className="font-display text-3xl mb-2">Who&apos;s Leading Right Now</h2>
        <p className="text-[var(--ff-text-dim)] text-sm max-w-2xl">
          Ranks every show by whichever business metric you pick. Uses each show&apos;s latest report card
          for the current value and the report before it for the growth arrow. Auto-refreshes every 30s.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 bg-[var(--ff-surface)] border border-[var(--ff-border)] p-4">
        <div className="flex items-center gap-3">
          <span className="label-eyebrow text-[var(--ff-text-dim)]">Rank by</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="p-2 bg-black/40 border border-[var(--ff-border)] font-mono-ui text-sm"
            data-testid="standings-metric-select"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-black/40 border border-[var(--ff-border)] hover:border-[var(--ff-red)]/50 transition-colors label-eyebrow disabled:opacity-50"
          data-testid="standings-refresh-btn"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh Now"}
        </button>
        <div className="flex items-center gap-2 text-xs text-[var(--ff-text-dim)] font-mono-ui" data-testid="standings-countdown">
          <Zap size={12} className="text-[var(--ff-red)]" />
          Auto in {countdown}s
        </div>
      </div>

      {/* Meta strip */}
      {data && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--ff-text-dim)] font-mono-ui" data-testid="standings-meta">
          <span>{data.total_with_data} of {data.rows.length} shows have report data</span>
          {generatedAt && <span>· Updated {generatedAt.toLocaleTimeString()}</span>}
          <span>· Ranking by <span className="text-white uppercase tracking-[0.15em]">{activeMetric.label}</span></span>
        </div>
      )}

      {loading ? (
        <div className="p-16 text-center text-[var(--ff-text-dim)] flex items-center justify-center gap-2 font-mono-ui text-xs uppercase tracking-[0.2em]">
          <Loader2 className="animate-spin" size={16} /> Loading standings…
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="border border-dashed border-[var(--ff-border)] p-16 text-center" data-testid="standings-empty">
          <Activity className="text-[var(--ff-text-dim)] mx-auto mb-3" size={32} />
          <div className="font-mono-ui text-sm text-[var(--ff-text-dim)]">
            No shows to rank yet. Add shows first, then enter report cards for them.
          </div>
        </div>
      ) : (
        <div className="grid gap-px bg-[var(--ff-border)]" data-testid="standings-rows">
          {data.rows.map((row, i) => (
            <StandingRow key={row.id} row={row} position={i} kind={activeMetric.kind} />
          ))}
        </div>
      )}
    </div>
  );
}

function StandingRow({ row, position, kind }) {
  const color = CATEGORY_COLORS[row.category] || "#FF3B30";
  const hasData = row.has_data;
  const g = row.growth_pct;
  let growthBadge = null;
  if (hasData && g !== null && g !== undefined) {
    const flat = Math.abs(g) < 1;
    const up = g > 0;
    const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
    const cls = flat ? "text-[var(--ff-text-dim)]" : up ? "text-emerald-400" : "text-[var(--ff-red)]";
    growthBadge = (
      <div className={`flex items-center gap-1 font-mono-ui text-xs ${cls}`} data-testid={`standings-growth-${row.id}`}>
        <Icon size={12} />
        {up && !flat ? "+" : ""}{g.toFixed(1)}%
      </div>
    );
  } else if (hasData) {
    growthBadge = (
      <div className="text-[10px] font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
        First report
      </div>
    );
  }

  return (
    <div
      className={`bg-[var(--ff-surface)] grid grid-cols-[auto_56px_1fr_auto_auto] gap-4 md:gap-6 items-center p-4 md:p-5 ${
        !hasData ? "opacity-50" : ""
      }`}
      data-testid={`standings-row-${row.id}`}
    >
      <div
        className="font-display text-3xl md:text-4xl w-10 md:w-14 text-center"
        style={{ color: hasData ? medalColor(position) : "var(--ff-text-dim)" }}
        data-testid={`standings-rank-${row.id}`}
      >
        {hasData ? String(position + 1).padStart(2, "0") : "—"}
      </div>
      <div
        className="relative aspect-square w-14 overflow-hidden shrink-0"
        style={{ background: `linear-gradient(135deg, ${color}44, ${color}11)` }}
      >
        {row.thumbnail_url ? (
          <img src={row.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center font-display text-base opacity-40" style={{ color }}>
            {row.title?.split(" ").map((w) => w[0]).slice(0, 3).join("")}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="font-display text-lg md:text-xl truncate">{row.title}</div>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[var(--ff-text-dim)]">
          {row.is_collab && <CollabBadge size="xs" testId={`standings-collab-${row.id}`} />}
          {row.category && (
            <span className="font-mono-ui uppercase tracking-[0.15em]" style={{ color }}>{row.category}</span>
          )}
          {row.latest_period_end && (
            <span className="font-mono-ui uppercase tracking-[0.15em]">
              {row.latest_period_type} · {row.latest_period_end}
            </span>
          )}
          {!hasData && (
            <span className="font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-red)]/80">
              No report yet
            </span>
          )}
        </div>
      </div>
      <div className="min-w-[80px] text-right">
        {growthBadge}
      </div>
      <div className="flex flex-col items-end min-w-[110px]">
        <div className="font-display text-3xl" data-testid={`standings-value-${row.id}`}>
          {formatMetric(kind, row.current_value)}
        </div>
        {row.prior_value !== null && row.prior_value !== undefined && (
          <div className="text-[10px] font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
            was {formatMetric(kind, row.prior_value)}
          </div>
        )}
      </div>
    </div>
  );
}
